import React, {useEffect, useState, useCallback} from 'react';
import 'react-day-picker/dist/style.css';
import styles from './update-shipping-date.module.scss';
import { useTranslation, Trans } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom'

import Footer from '../../components/Footer';
import {emailPattern} from '../../util';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from '../../components/button';
import DefaultLoader from "../../components/defaultLoader"
import {SubmitHandler, useForm} from 'react-hook-form';
import Message from '../../components/message';

import { DayPicker } from 'react-day-picker';

import moment from 'moment';

const throw4XX5XXError = (response: any, t: (key: string, value: string) => string) => {
  switch (response.status) {
    case 400:
      throw new Error(
        t(
          '400_bad_request_error',
          'We\'re sorry, there was an error with your request. Please try again.'
        )
      );
    case 404:
      throw new Error(
        t(
          'order_not_found',
          'We’re sorry, but we can’t find that order number. Please double check your order and email address and try again.'
        )
      );
    case 406:
      throw new Error(
        t(
          'rental_order_not_applicable',
          'We’re sorry, in case you want to change rental shipping date, please do it through Rental Portal.'
        )
      );
    default:
      throw new Error(
        t(
          'generic_error',
          'Oops…something went wrong! Please check your email address and order number and try again. If you need help, please connect with us at customercare@happiestbaby.com.'
        )
      );
  }
}

interface IFormInputs {
  email: string;
  orderNumber: string;
}

const UpdateShippingDatePage = () => {
  const {t} = useTranslation();
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestFailed, setRequestFailed] = useState('');

  const [startDate, setStartDate] = useState<Date>();

  const [searchParams] = useSearchParams();
  const [showSpinner, setShowSpinner] = useState(false)
  const currentQueryParams = Object.fromEntries(searchParams.entries())

  const {
    register,
    handleSubmit,
    formState: {errors, isValid}
  } = useForm<IFormInputs>({
    mode: 'onChange'
  });

  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    // todo, remove region check once recaptcha token is generated on EU region
    if (process.env.REACT_APP_REGION === 'us') {
      if (!recaptchaPassed) {
        setRecaptchaError('Please verify ReCAPTCHA');
        return;
      }
    }
    setButtonDisabled(true);
    changeShippingDate(data)
      .then((res) => {
        setRequestSuccess(true);
        setRequestFailed('');
      })
      .catch(e => {
        setRequestSuccess(false);
        setRequestFailed(e);
      })
      .finally(() => {
        setButtonDisabled(false);
      })
  };

  interface IPayload {
    email: string;
    orderNumber: string;
  }

  const changeShippingDate = async (data: IPayload) => {
    const url = `${process.env.REACT_APP_ORDER_SERVICE_API}/self-service/orders/${encodeURIComponent(data.orderNumber.trim())}`;
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: data.email,
        shippingDate: moment(startDate).format('YYYY-MM-DD')
      })
    });

    if (response.status >= 400) {
      throw4XX5XXError(response, t)
    }
    return response;
  }

  const confirmShippingDate = useCallback(async (usdt: String) => {
    const url = `${process.env.REACT_APP_ORDER_SERVICE_API}/self-service/orders/confirm-shipping-date`;
    try {
      setShowSpinner(true)
      const response = await fetch(url, {
        method: 'put',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: usdt.replaceAll(' ', '+'),
        })
      })

      if (response.status >= 400) {
        throw4XX5XXError(response, t)
      }
      setRequestSuccess(true);
      setRequestFailed('');
    } catch (err: any) {
      setRequestSuccess(false);
      setRequestFailed(err.message);
    } finally {
      setShowSpinner(false)
    }
  }, [t]);

  useEffect(() => {
    if (recaptchaPassed) {
      setRecaptchaError('');
    }
  }, [recaptchaPassed]);

  useEffect(() => {
    setStartDate(new Date());
  }, [])

  useEffect(() => {
    if (currentQueryParams.usdt) {
      confirmShippingDate(currentQueryParams.usdt)
    } else {
      setRequestSuccess(false)
      setRequestFailed('')
    }
  }, [currentQueryParams.usdt, confirmShippingDate])

  const disabledDays = (date: moment.MomentInput) => {
    const now = moment().format('YYYY-MM-DD');
    const isTodayOrInPast = moment(date).isSameOrBefore(now);

    const day = moment(date).days();
    const isWeekend = day === 0 || day === 6;

    const maxDateLimitMonths = process.env.REACT_APP_MAX_MONTHS_IN_ADVANCE_SHIPPING_DATE || "6";

    const maxMonthsInAdvance = moment().add(parseInt(maxDateLimitMonths, 10), 'months');
    const isMoreThenMaxMonthsInAdvanced = moment(date).isAfter(maxMonthsInAdvance);

    return isTodayOrInPast ||
      isWeekend ||
      isMoreThenMaxMonthsInAdvanced;
  }

  const additionalFooterMessage = (<p>
    {t('terms_and_condition_intro', {
      defaultValue:
        'For all retail terms and conditions, please visit'
    })}
    <a href={process.env.REACT_APP_RETAIL_TERMS_AND_CONDITIONS_LINK}>
      &nbsp;{t('retail_terms_and_cond', 'this page')}
    </a>
  </p>);

  const form = (<>
        <div className="d-flex container justify-content-center">
          <div className={styles.HeaderSection}>
            <p>
              {t('zzs_for_freex', {
                defaultValue:
                  'Please fill in the form to change the ship date for your SNOO order. If you want to modify SNOO rental, you must log into your'
              })}
              <a href={process.env.REACT_APP_LOGIN_URL}>
                &nbsp;{t('rental_portal_link', 'Rental portal')}
              </a>
            </p>
            <p>
              {t('order_processing_info', {
                defaultValue:
                  'The ship date you select is when your order starts processing rather than the delivery date. You can expect the order to spend up to 5-10 business days shipping and processing from the selected ship date.'
              })}
            </p>
          </div>
        </div>
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="mt-3">
          <p className={'form-field-title mb-3 form-field-title'}>
            <span className={'text-danger'}>*</span>
            {t('personal_information', 'Personal Information')}
          </p>
          <div className="mb-2 form-floating custom-input">
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              id="email"
              placeholder="name@example.com"
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email', {
                required: {
                  value: true,
                  message: t('email_input_error_required', 'Email is required')
                },
                pattern: {
                  value: emailPattern,
                  message: t(
                    'email_input_error_pattern',
                    'You have entered an invalid e-mail address. Please enter a valid email.'
                  )
                }
              })}
            />
            <label
              htmlFor="email"
              className={'font-size-sm'}
            >
              {t('email_input_label', 'Your email')}
            </label>
            {errors.email && (
              <p className="invalid-feedback d-block">{errors.email.message}</p>
            )}
          </div>
          <p className={'text-helper'}>
            *{' '}
            {t(
              'email_helper',
              'Please enter the email you used when you purchased your SNOO or placed your SNOO order'
            )}
          </p>
          <div className="mb-3 form-floating custom-input">
            <input
              className={`form-control ${errors.orderNumber ? 'is-invalid' : ''}`}
              type="text"
              placeholder={t('order_number', 'Order number')}
              aria-label="input order number"
              {...register('orderNumber', {
                required: {
                  value: true,
                  message: t(
                    'order_number_error_required',
                    'Order number is required.'
                  )
                },
                minLength: {
                  value: 4,
                  message: t(
                    'invalid_order_number_entered',
                    'You have entered an invalid order number. Please check your input and try again.'
                  )
                },
                validate: value => !!value.trim() || t('input_with_only_whitespaces', 'Entered value can\'t contain only white spacing'),
              })}
            />
            <label
              htmlFor="orderNumber"
              className={'font-size-sm'}
            >
              {t('order_number_label', 'Order Number')}
            </label>
            {errors.orderNumber && (
              <p className="invalid-feedback d-block">
                {errors.orderNumber.message}
              </p>
            )}
          </div>
          <p className={'text-helper'}>
            *{' '}
            {t(
              'order_helper',
              'You can find your SNOO order number in your confirmation email.'
            )}
          </p>
          <DayPicker
            className={styles.Datepicker}
            mode="single"
            selected={startDate}
            onSelect={setStartDate}
            disabled={disabledDays}
          />
          <div className={styles.Recaptcha + ' d-flex container justify-content-center'}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}
              onChange={(v: string | null) => setRecaptchaPassed(!!v)}
            />
          </div>
          {requestSuccess && (
            <Message type='success-light' fullWidth={true}>
              {t(
                'send_confirmation_mail',
                'We will send you an email. Please check your email to finalize updating shipping date. In case you do not receive email in next 5-10 minutes please submit form again.'
              )}
            </Message>
          )}
          {requestFailed && (
            <Message type='error-light' fullWidth={true}>
              {`${requestFailed}`}
            </Message>
          )}
          <div className={'d-flex container justify-content-center'}>
            {!!recaptchaError && (
              <Message type='error-light' fullWidth={true}>
                {`${recaptchaError}`}
              </Message>
            )}
          </div>
          <div className='button-wrapper d-flex container justify-content-center'>
            <Button
              color={'red'}
              onClick={() => {}}
              text={t('btn_submit', 'Submit shipping date')}
              name='submit'
              disabled={buttonDisabled || !isValid}
              showSpinner={buttonDisabled}
            />
          </div>
        </form>
  </>)

  return (
    <>
      <header
        role="banner"
        className={
          styles.SelfServiceContainer +
          ' h-100 justify-content-center align-items-center mx-auto'
        }
      >
        <div className="d-flex container justify-content-center mb-20">
          <h1 className={styles.Title}>
            {t('update_retail_shipping_date', 'Update retail SNOO shipping date')}
          </h1>
        </div>
       {currentQueryParams.usdt ? <>
       {showSpinner ? <div className="d-flex container justify-content-center"><DefaultLoader color='secondary' type='block' /></div> : <>
          {requestSuccess && (
            <Message type='success-light' fullWidth={true}>
              {t(
                'update_shipping_date_request_success',
                'Your request to change shipping date has been sent successfully. Please check your email for shipping date update confirmation. Thank you for using our services.'
              )}
            </Message>
          )}
          {requestFailed && (<>
            <Message type='error-light' fullWidth={true}>
              {`${requestFailed}`}
            </Message>
            <Trans i18nKey='redirect_page_to_update_shipping_date_page_in_case_of_error' t={t}>
              If you still want to update shipping date to another date, please <Link to='/update-shipping-date'>go to form page</Link>
            </Trans>
          </>)}
       </>}
       </> : form}
        <Footer additionalFooterMessage={additionalFooterMessage}/>
      </header>
    </>
  );
};

export default UpdateShippingDatePage;
